import { useMemo, useState } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { IPublicClientApplication } from "@azure/msal-browser";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { callPymbleApi } from "./usePymbleApi";

/**
 * My Pymble's federated search — the one behind the portal navbar.
 *
 * `GET search` runs every requested scope in parallel against one query and
 * returns them grouped, under a hard three-second budget: scopes that finish in
 * time come back even if a slower one is still running.
 *
 * Why this rather than `contact/admin/search` + `group/search/`, which is what
 * the attendee pickers used to call:
 *
 *   - One request instead of two, and one relevance ranking instead of two
 *     lists stapled together.
 *   - The scopes are exactly the audience chips — student, staff, parent,
 *     group — so a chip maps to a scope rather than to a bespoke filter.
 *   - Someone searching here gets the same results, in the same order, as the
 *     portal's navbar. Two search boxes over the same people that disagree is
 *     the thing worth avoiding.
 *
 * Contact ids are `ContactPersonal._id`, the same id space the attendee
 * endpoints take — federated search reads `Migration.ContactPersonal`, and
 * `contact/admin/search` and the attendee link helper both use the same
 * collection. Checked before switching, because an id from the wrong space
 * would come back from `POST .../attendees/` as `Unknown` and look like a
 * search problem rather than an id problem.
 *
 * ⚠️ PII. Hits are names, year levels and staff titles. React Query cache
 * (memory) for the life of the dialog and nowhere else; never logged.
 */

const SEARCH = "search";

/** The API's own floor. Shorter than this is not worth a round trip. */
export const MIN_QUERY_LENGTH = 2;

/** Scopes this app asks for. The endpoint offers nine; these are the people. */
export type SearchScope = "student" | "staff" | "parent" | "group";

/** One hit, in the shape every scope returns. */
export interface SearchHit {
	id: string;
	scope: SearchScope | string;
	title: string;
	subtitle?: string;
	email?: string;
	/** Groups only. */
	group_type_label?: string;
}

interface SearchResponse {
	query: string;
	results: Record<string, SearchHit[]>;
	total: number;
}

/**
 * One search, as a plain call.
 *
 * Split out of the hook below so the assistant can run a search of its own
 * without borrowing the picker's search box: `useFederatedSearch` is driven by
 * typed state and React Query, which is right for a combobox and wrong for
 * "look these two names up because the model asked". Both go through here, so
 * the endpoint, the scope ordering and the flattening are defined once and the
 * two paths cannot drift into returning different results for one query.
 */
export async function fetchFederatedSearch(
	instance: IPublicClientApplication,
	term: string,
	scopes: SearchScope[],
	limit = 20
): Promise<SearchHit[]> {
	const trimmed = term.trim();
	if (trimmed.length < MIN_QUERY_LENGTH || !scopes.length) return [];

	/* Asked for in the caller's order, so the flattened list comes back in it. */
	const scopeList = scopes.join(",");
	const res: SearchResponse | null = await callPymbleApi(
		instance,
		"GET",
		`${SEARCH}?q=${encodeURIComponent(trimmed)}` +
			`&scopes=${encodeURIComponent(scopeList)}&limit=${limit}`
	);
	if (!res?.results) return [];

	// Flattened in the order the caller listed its scopes — which is the
	// order the audience chips appear in — rather than whatever order
	// the scopes happened to finish in.
	return scopeList
		.split(",")
		.flatMap((scope) => res.results[scope] ?? [])
		.filter((hit) => hit?.id && hit?.title);
}

export function useFederatedSearch(scopes: SearchScope[], limit = 20) {
	const { instance } = useMsal();
	const isAuthenticated = useIsAuthenticated();
	const [term, setTerm] = useState("");

	const trimmed = term.trim();
	const longEnough = trimmed.length >= MIN_QUERY_LENGTH;
	/* The cache key sorts, though the request does not — ticking chips in a
	   different order is the same search and should not miss the cache. */
	const cacheKey = useMemo(() => [...scopes].sort().join(","), [scopes]);

	const query = useQuery({
		queryKey: ["federated-search", trimmed, cacheKey, limit],
		queryFn: () => fetchFederatedSearch(instance, trimmed, scopes, limit),
		enabled: isAuthenticated && longEnough && scopes.length > 0,
		// Hold the last results while the next query runs, so the list does not
		// blank out between keystrokes.
		placeholderData: keepPreviousData,
	});

	return {
		term,
		setTerm,
		longEnough,
		minQueryLength: MIN_QUERY_LENGTH,
		hits: useMemo(() => query.data ?? [], [query.data]),
		searching: longEnough && query.isFetching,
		searchFailed: !!query.error,
	};
}
